import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Text,
  Textarea,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import { UserContext } from '../../providers'

interface Props {
  isOpen: boolean
  onClose(): void
}

interface FormProps {
  query: string
}

const SupportRequest = ({ isOpen, onClose }: Props): React.ReactElement => {
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const toast = useToast()
  const { user } = useContext(UserContext)
  const { t } = useTranslation()
  const [formError, setFormError] = useState<string>('')

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <Formik
          initialValues={{
            query: t(
              'invoice_support_initial_text',
              'Hi\n\nI have a query regarding my invoice.',
            ),
          }}
          onSubmit={async (values): Promise<void> => {
            setFormError('')
            if (user?.clientNumber) {
              await request.post(`/api/email/${user.clientNumber}/send`, {
                to: 'support@imagekit.io',
                subject: t(
                  'invoice_support_request_subject',
                  'Invoice support request',
                ),
                text: values.query
                  .replace(/&/gu, '&amp;')
                  .replace(/</gu, '&lt;')
                  .replace(/"/gu, '&quot;'),
              })

              if (request.error) {
                setFormError(
                  t(
                    'error_message_could_not_be_sent',
                    'Message could not be sent.',
                  ),
                )
              } else {
                toast({
                  title: t(
                    'success_message_sent_title',
                    'We have got your message!',
                  ),
                  description: t(
                    'success_message_sent_description',
                    'Someone from our team will contact you shortly',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                setFormError('')
                onClose()
              }
            }
          }}
          validationSchema={Yup.object().shape({
            query: Yup.string().required('Required'),
          })}
        >
          {({ handleChange, isSubmitting, submitForm }) => (
            <>
              <ModalHeader>
                {t(
                  'invoice_support_request_heading',
                  'Invoice support request',
                )}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody spacing={10}>
                {formError ? (
                  <Alert status="error" mb="1em" minWidth="unset">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <Text color="appBattleshipGrey">
                  {t(
                    'invoices_support_request_intro',
                    `Let us know if you have any query regarding your invoices and 
              we will be glad to answer it for you.\nAll follow-up conversations will be sent 
              to your email.`,
                  )}
                </Text>
                <Field name="query">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="1em"
                      isInvalid={Boolean(
                        form.errors.query && form.touched.query,
                      )}
                      isRequired
                    >
                      <FormLabel htmlFor="query">
                        {t('query', 'Query')}
                      </FormLabel>
                      <Textarea
                        {...field}
                        id="query"
                        onChange={handleChange}
                        placeholder={t(
                          'placeholder_your_message_here',
                          'Your message here',
                        )}
                        size="sm"
                      />
                      <FormErrorMessage marginBottom="-2.05em">
                        {form.errors.query}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button
                  onClick={() => {
                    onClose()
                    setFormError('')
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('send', 'Send')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default SupportRequest
